// react
import { useContext, useState, useEffect } from "react";

// context
import ProductContext from "../../context/ProductContext";

// helpers
import { legacyAddToCart } from "../../helpers";

// components
import Button from "../button/Button";
import LoginLink from "../loginLink/LoginLink";
import AddToCartModal from "../AddToCartModal/AddToCartModal";
import { ModalWrapper, Modal, useTriggerState } from "../modal/Modal";

// styles
import "./pricing.styles.scss";

interface TableCellProps {
  Label?: string;
  Value?: any;
  AdditionalInfo?: string;
  className?: string | undefined;
  children?: any;
}

const TableCell = ({
  Label,
  Value,
  AdditionalInfo,
  className,
  children,
}: TableCellProps) => (
  <div className={`_pricing_component_table_cell ${className}`}>
    {Label ? (
      <div className="_pricing_component_table_cell_label">{Label}</div>
    ) : null}

    {Value ? (
      <div className="_pricing_component_table_cell_value">{Value}</div>
    ) : null}

    {AdditionalInfo ? (
      <div className="_pricing_component_table_cell_info caption hdg_darkgray">
        {AdditionalInfo}
      </div>
    ) : null}
    {children}
  </div>
);

function PricingSection() {
  const context: any = useContext(ProductContext);

  const {
    productState: {
      SAPLeadTime,
      IsUserLoggedIn,
      SAPPricingInfo,
      Quantity,
      ProductNumber,
      ProductStatus,
      User,
      PricingStatusMessage,
    },
  }: {
    productState: {
      SAPLeadTime: any;
      IsUserLoggedIn: boolean;
      SAPPricingInfo: any;
      ProductNumber: string;
      ProductStatus: any[];
      Quantity: number;
      User: any;
      PricingStatusMessage: string;
    };
  } = context;

  const handleAddToCartBtnOnClick = () =>
    legacyAddToCart(ProductNumber, Quantity, "", "order");

  const [unitPrice, setUnitPrice] = useState(null);
  const [unitMSRP, setUnitMSRP] = useState(null);
  const [totalPriceStr, setTotalPriceStr] = useState("");
  const [totalMSRPStr, setTotalMSRPStr] = useState("");
  const [localQty, setLocalQty] = useState(Quantity);

  const currencyConverter = (type: any, formatted: any) =>
    SAPPricingInfo?.RawPrice !== 0
      ? `${SAPPricingInfo?.Currency}${type?.toLocaleString(undefined, {
          minimumFractionDigits: 2,
        })} ${SAPPricingInfo?.CurrencyCode}`
      : formatted ?? "";

  const totalCalculator = (type: any) => (type ? type * (localQty ?? 1) : 0);

  const quantityHandler = () => {
    const totalPrice = totalCalculator(SAPPricingInfo?.RawPrice);
    const totalMSRP = totalCalculator(SAPPricingInfo?.RawMSRP);

    const unitPrice: any = currencyConverter(
      SAPPricingInfo?.RawPrice,
      SAPPricingInfo?.FormattedPrice,
    );

    setUnitPrice(unitPrice);

    const unitMSRP: any = currencyConverter(
      SAPPricingInfo?.RawMSRP,
      SAPPricingInfo?.FormattedMSRP,
    );

    setUnitMSRP(unitMSRP);

    const totalPriceStr: any = currencyConverter(
      totalPrice,
      SAPPricingInfo?.FormattedPrice,
    );

    setTotalPriceStr(totalPriceStr);

    const totalMSRPStr: any = currencyConverter(
      totalMSRP,
      SAPPricingInfo?.FormattedMSRP,
    );

    setTotalMSRPStr(totalMSRPStr);
  };

  const showPriceMessage = () => {
    return (
      ProductStatus[0]?.ProductStatus === "Coming Soon" &&
      SAPPricingInfo?.RawPrice === 0
    );
  };

  useEffect(() => {
    setLocalQty(Quantity);
    // eslint-disable-next-line
  }, [Quantity, ProductNumber]);

  useEffect(() => {
    quantityHandler();
    // eslint-disable-next-line
  }, [localQty, ProductNumber]);

  const TriggerModal = () => {
    const { isActive, onClick } = useTriggerState();

    return (
      <Button
        className={`_pricing_component_add-to-cart_button ${
          isActive ? "isActive" : ""
        }`}
        onClick={() => {
          onClick();
          handleAddToCartBtnOnClick();
        }}
      >
        Add to Cart
      </Button>
    );
  };

  return (
    <>
      {SAPPricingInfo ? (
        <>
          <div className="_pricing_component">
            {showPriceMessage() ? (
              <p
                className="_pricing_component_message"
                dangerouslySetInnerHTML={{
                  __html: PricingStatusMessage ?? "",
                }}
              ></p>
            ) : (
              <>
                <div className="_pricing_component_table">
                  <div className="_pricing_component_table_sub">
                    <TableCell Label={"Quantity"} Value={Quantity} />
                    <TableCell Label={"Unit of Measure"} Value={"Each"} />
                    {/* <TableCell Label={"Min. Order Qty"} Value={12} /> */}
                  </div>
                  <div className="_pricing_component_table_sub">
                    {SAPPricingInfo && IsUserLoggedIn ? (
                      <>
                        <TableCell
                          className="_pricing_component_table_second"
                          Label={"Unit Price"}
                          Value={unitPrice}
                          AdditionalInfo={`MSRP: ${unitMSRP}`}
                        />
                        <TableCell
                          className="_pricing_component_table_second"
                          Label={"Total Price"}
                          Value={totalPriceStr}
                          AdditionalInfo={`MSRP: ${totalMSRPStr}`}
                        />
                        {/* <TableCell className="_pricing_component_table_second hide-on-desktop" />  //uncomment this when we have min qty*/}
                      </>
                    ) : (
                      <TableCell
                        className="_pricing_component_table_second_loggedout"
                        Label={"Unit Price"}
                      >
                        <LoginLink />
                      </TableCell>
                    )}
                  </div>
                </div>
                {!SAPLeadTime?.IsAddToCartHidden &&
                  ProductStatus[0]?.ShowAddToCart && (
                    <div className="_pricing_component_add-to-cart">
                      {SAPPricingInfo &&
                      IsUserLoggedIn &&
                      !User?.IsUserLimited ? (
                        <ModalWrapper>
                          <TriggerModal />
                          <Modal>
                            <AddToCartModal />
                          </Modal>
                        </ModalWrapper>
                      ) : null}
                    </div>
                  )}
              </>
            )}
          </div>
        </>
      ) : null}
    </>
  );
}

export default PricingSection;
